import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import TenantLayout from '../components/TenantLayout';
import { useSite } from '../context/SiteContext';

const TenantStaff = () => {
  const { sites, selectedSite, updateTrigger } = useSite();
  const [staff, setStaff] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'Ticket Counter',
    siteId: '',
    shiftId: ''
  });
  const [shifts, setShifts] = useState([]);
  const [showShiftForm, setShowShiftForm] = useState(false);
  const [shiftForm, setShiftForm] = useState({ name: '', startTime: '', endTime: '' });

  useEffect(() => {
    fetchStaff();
    fetchShifts();
  }, [selectedSite, updateTrigger]);

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await axios.get(`${API_BASE_URL}/api/tickets/staff`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'x-tenant-id': user.tenantId || 'default'
        }
      });
      setStaff(response.data.staff || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
      setStaff([]);
    }
  };

  const fetchShifts = async () => {
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await axios.get(`${API_BASE_URL}/api/tickets/shifts`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'x-tenant-id': user.tenantId || 'default'
        }
      });
      setShifts(response.data.shifts || []);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      setShifts([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (editingStaff) {
        await axios.put(`${API_BASE_URL}/api/tickets/staff/${editingStaff._id}`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'x-tenant-id': user.tenantId || 'default'
          }
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/tickets/staff`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'x-tenant-id': user.tenantId || 'default'
          }
        });
      }
      
      await fetchStaff();
      resetForm();
    } catch (error) {
      if (error.response?.data?.action === 'reactivate') {
        const confirm = window.confirm(
          `Staff with email ${formData.email} exists but is inactive. Do you want to reactivate them with new details?`
        );
        if (confirm) {
          try {
            const token = localStorage.getItem('token');
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            await axios.post(`${API_BASE_URL}/api/tickets/staff/${error.response.data.staffId}/reactivate`, formData, {
              headers: { 
                Authorization: `Bearer ${token}`,
                'x-tenant-id': user.tenantId || 'default'
              }
            });
            await fetchStaff();
            resetForm();
          } catch (reactivateError) {
            alert('Error reactivating staff: ' + (reactivateError.response?.data?.message || reactivateError.message));
          }
        }
      } else {
        alert('Error saving staff: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to remove this staff member?')) {
      try {
        const token = localStorage.getItem('token');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        await axios.delete(`${API_BASE_URL}/api/tickets/staff/${id}`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'x-tenant-id': user.tenantId || 'default'
          }
        });
        fetchStaff();
      } catch (error) {
        alert('Error deleting staff: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const startEdit = (staffMember) => {
    setFormData({
      name: staffMember.name,
      email: staffMember.email,
      phone: staffMember.phone,
      role: staffMember.role,
      siteId: staffMember.siteId?._id || staffMember.siteId,
      shiftId: staffMember.shiftId?._id || staffMember.shiftId
    });
    setEditingStaff(staffMember);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'Ticket Counter',
      siteId: '',
      shiftId: ''
    });
    setShowAddForm(false);
    setEditingStaff(null);
  };

  return (
    <TenantLayout>
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <div>
            <h1 style={{ margin: '0 0 10px 0', color: '#2d3748', fontSize: '28px' }}>Staff Management</h1>
            <p style={{ color: '#718096', margin: 0 }}>Manage staff members and their assignments</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            style={{
              background: 'linear-gradient(45deg, #14b8a6, #0d9488)',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)'
            }}
          >
            + Add Staff
          </button>
        </div>

        {showAddForm && (
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            marginBottom: '24px',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#2d3748' }}>{editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <input 
                  type="text" 
                  placeholder="Full Name" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }} 
                />
                <input 
                  type="email" 
                  placeholder="Email Address" 
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }} 
                />
                <input 
                  type="tel" 
                  placeholder="Phone Number" 
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }} 
                />
                <select 
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                >
                  <option value="Ticket Counter">Ticket Counter</option>
                  <option value="Security Guard">Security Guard</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Manager">Manager</option>
                </select>
                <select 
                  value={formData.siteId}
                  onChange={(e) => setFormData({...formData, siteId: e.target.value})}
                  required
                  style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                >
                  <option value="">Assign to Site</option>
                  {sites.map(site => (
                    <option key={site._id} value={site._id}>{site.name}</option>
                  ))}
                </select>
                <div style={{ display: 'flex', gap: '8px', gridColumn: 'span 2' }}>
                  <select 
                    value={formData.shiftId}
                    onChange={(e) => setFormData({...formData, shiftId: e.target.value})}
                    required
                    style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', flex: 1 }}
                  >
                    <option value="">Select Shift</option>
                    {shifts.map(shift => (
                      <option key={shift._id} value={shift._id}>
                        {shift.name} ({shift.startTime} - {shift.endTime})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowShiftForm(true)}
                    style={{
                      background: 'linear-gradient(45deg, #8b5cf6, #7c3aed)',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    + Shift
                  </button>
                </div>
              </div>
              <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                <button type="submit" style={{
                  background: 'linear-gradient(45deg, #14b8a6, #0d9488)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}>
                  {editingStaff ? 'Update Staff' : 'Add Staff'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    background: '#e2e8f0',
                    color: '#4a5568',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {showShiftForm && (
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            marginBottom: '24px',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#2d3748' }}>Add New Shift</h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const token = localStorage.getItem('token');
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                await axios.post(`${API_BASE_URL}/api/tickets/shifts`, shiftForm, {
                  headers: { 
                    Authorization: `Bearer ${token}`,
                    'x-tenant-id': user.tenantId || 'default'
                  }
                });
                fetchShifts();
                setShiftForm({ name: '', startTime: '', endTime: '' });
                setShowShiftForm(false);
              } catch (error) {
                alert('Error creating shift: ' + (error.response?.data?.message || error.message));
              }
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                <input 
                  type="text" 
                  placeholder="Shift Name" 
                  value={shiftForm.name}
                  onChange={(e) => setShiftForm({...shiftForm, name: e.target.value})}
                  required
                  style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }} 
                />
                <input 
                  type="time" 
                  placeholder="Start Time" 
                  value={shiftForm.startTime}
                  onChange={(e) => setShiftForm({...shiftForm, startTime: e.target.value})}
                  required
                  style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }} 
                />
                <input 
                  type="time" 
                  placeholder="End Time" 
                  value={shiftForm.endTime}
                  onChange={(e) => setShiftForm({...shiftForm, endTime: e.target.value})}
                  required
                  style={{ padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }} 
                />
              </div>
              <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                <button type="submit" style={{
                  background: 'linear-gradient(45deg, #8b5cf6, #7c3aed)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}>
                  Add Shift
                </button>
                <button
                  type="button"
                  onClick={() => setShowShiftForm(false)}
                  style={{
                    background: '#e2e8f0',
                    color: '#4a5568',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: 'linear-gradient(45deg, #14b8a6, #0d9488)' }}>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Staff ID</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Role</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Assigned Site</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Phone</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Shift</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map(member => (
                <tr key={member._id}>
                  <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>{member._id.slice(-6)}</td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>{member.name}</td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>{member.role}</td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>{member.siteId?.name || 'N/A'}</td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>{member.phone}</td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                    {member.shiftId?.name} ({member.shiftId?.startTime} - {member.shiftId?.endTime})
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{
                      background: member.status === 'active' ? 'linear-gradient(45deg, #10b981, #059669)' : 'linear-gradient(45deg, #ef4444, #dc2626)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {member.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button 
                        onClick={() => startEdit(member)}
                        style={{
                          background: 'linear-gradient(45deg, #0891b2, #0e7490)',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(member._id)}
                        style={{
                          background: 'linear-gradient(45deg, #ef4444, #dc2626)',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {staff.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)',
            borderRadius: '16px',
            border: '1px solid #a7f3d0'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '16px' }}>👥</div>
            <h3 style={{ color: '#065f46', marginBottom: '8px' }}>No staff members found</h3>
            <p style={{ color: '#047857' }}>No staff members found for selected site</p>
          </div>
        )}
      </div>
    </TenantLayout>
  );
};

export default TenantStaff;